import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import '../../../styles/AvaChat.css';
import avaAvatar from './ava-avatar.png';

// Browser-side chat for the «آوا» admin assistant. Talks ONLY to our own
// /api/ava/* reverse-proxy (server/routes/ava.js); the upstream hermes
// api_server and the AVA_API_KEY are never visible here. The proxy gates each
// call with the Supabase admin JWT and streams the upstream SSE straight back.
// Exported: AvaHistory (resume) and AvaTab (new chat) write/clear this same key.
export const SESSION_KEY = 'ava_session_id';

// Longest side an attached image is downscaled to before upload. Keeps the
// data URL small so it fits comfortably under the upstream 10MB request cap.
const MAX_IMAGE_DIM = 1600;
// JPEG quality for the canvas re-encode. 0.85 is visually clean yet compact.
const IMAGE_JPEG_QUALITY = 0.85;
// Hard ceiling on the encoded data URL length. Even after downscaling a busy
// photo can stay large; above this we refuse it rather than risk a rejected
// 10MB upstream request.
const MAX_DATA_URL_CHARS = 8_000_000;

// Starter prompts shown on the empty welcome screen. Clicking one drops it into
// the input (focused) so the admin can edit or send straight away. Phrased for
// the FTTX admin surface Ava can actually read.
const SUGGESTIONS = [
  'متقاضیان در انتظار چند نفرند؟',
  'گزارش اپراتورها را خلاصه کن',
  'لیست انتظار را بررسی کن',
];

// Build the chat-turn `message` payload for the upstream hermes api_server.
// With no attachment we send the bare string (unchanged legacy shape). With an
// image we send the content-parts array the api_server also accepts:
// [{type:'text',text}, {type:'image_url',image_url:{url:dataUrl}}]. The text
// part is always present (possibly empty) so an image-only turn is still valid.
export function buildMessagePayload(text, imageDataUrl) {
  if (!imageDataUrl) return text;
  return [
    { type: 'text', text: text || '' },
    { type: 'image_url', image_url: { url: imageDataUrl } },
  ];
}

// Classify a file path/URL by extension so we know how to render it: an image
// inline, audio/video with controls, anything else as a download link.
export function mediaKind(pathOrUrl) {
  const base = String(pathOrUrl || '').split('?')[0];
  const dot = base.lastIndexOf('.');
  const ext = dot >= 0 ? base.slice(dot + 1).toLowerCase() : '';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext)) return 'image';
  if (['mp3', 'ogg', 'opus', 'wav', 'm4a', 'aac'].includes(ext)) return 'audio';
  if (['mp4', 'webm', 'mov'].includes(ext)) return 'video';
  return 'file';
}

// Last path segment, for a human-friendly file name / download attribute.
function baseName(pathOrUrl) {
  const base = String(pathOrUrl || '').split('?')[0].replace(/\/+$/, '');
  const slash = base.lastIndexOf('/');
  return slash >= 0 ? base.slice(slash + 1) : base;
}

// Short local time (HH:MM, Persian locale) shown next to each message. Tolerant
// of a missing/invalid timestamp so any older message without one renders blank
// rather than throwing.
export function formatTime(ts) {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' });
  } catch (_) {
    return '';
  }
}

// Pull Ava's file tags out of a reply. Ava sends a file by writing it under its
// cache dir and emitting `MEDIA:<absolute-path>` (or `MEDIA:https://...`) in the
// text; a leading `[[audio_as_voice]]` hint may precede an audio tag. Returns
// the display text with every tag + hint stripped (and blank runs collapsed)
// plus the list of referenced files. Exported so it is unit-testable.
export function extractMedia(text) {
  if (typeof text !== 'string' || !text) return { clean: text || '', media: [] };
  const media = [];
  const re = /MEDIA:\s*(\S+)/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    const p = m[1];
    if (!media.some((x) => x.path === p)) media.push({ path: p, isRemote: /^https?:\/\//i.test(p) });
  }
  const clean = text
    .replace(/\[\[audio_as_voice\]\]/gi, '')
    .replace(/MEDIA:\s*\S+/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return { clean, media };
}

// Render one file Ava shared. Local paths are fetched as a Blob through the
// admin-gated /api/ava/file route (so the Bearer JWT is sent and the object URL
// is revoked on unmount); remote URLs are used directly with no auth. Exported
// so the history transcript (AvaHistory) reuses the identical media rendering.
export function AvaMedia({ item }) {
  const mpath = item.path;
  const isRemote = !!item.isRemote;
  const kind = mediaKind(mpath);
  const name = baseName(mpath) || 'file';
  const [url, setUrl] = useState(isRemote ? mpath : '');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (isRemote) { setUrl(mpath); return undefined; }
    let cancelled = false;
    let objUrl = '';
    (async () => {
      try {
        const token = await getAccessToken();
        if (!token) throw new Error('no-token');
        const res = await fetch('/api/ava/file?path=' + encodeURIComponent(mpath), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('http-' + res.status);
        const blob = await res.blob();
        if (cancelled) return;
        objUrl = URL.createObjectURL(blob);
        setUrl(objUrl);
      } catch (_) {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objUrl && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(objUrl);
    };
  }, [mpath, isRemote]);

  if (failed) {
    return <div className="ava-media-fail">دریافت فایل ناموفق بود: {name}</div>;
  }
  if (!url) {
    return <div className="ava-media-loading">در حال بارگذاری فایل: {name}</div>;
  }
  if (kind === 'image') {
    return <img className="ava-media-image" src={url} alt={name} />;
  }
  if (kind === 'audio') {
    return <audio className="ava-media-audio" src={url} controls preload="metadata" />;
  }
  if (kind === 'video') {
    return <video className="ava-media-video" src={url} controls preload="metadata" />;
  }
  return (
    <a className="ava-media-download" href={url} download={name}>
      دانلود فایل: {name}
    </a>
  );
}

// Downscale an image File client-side via a canvas and return a JPEG data URL.
// The longest side is clamped to MAX_IMAGE_DIM; smaller images are not upscaled.
// Resolves to a `data:image/jpeg;base64,...` string.
function downscaleImageToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const { width, height } = img;
      const longest = Math.max(width, height) || 1;
      const scale = longest > MAX_IMAGE_DIM ? MAX_IMAGE_DIM / longest : 1;
      const w = Math.max(1, Math.round(width * scale));
      const h = Math.max(1, Math.round(height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('canvas-unsupported')); return; }
      ctx.drawImage(img, 0, 0, w, h);
      try {
        resolve(canvas.toDataURL('image/jpeg', IMAGE_JPEG_QUALITY));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image-load-failed'));
    };
    img.src = url;
  });
}

// Read the current Supabase access token. Admin gating happens server-side; we
// just need a Bearer to send. Returns null when no session is present.
async function getAccessToken() {
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token || null;
}

// Split a growing SSE buffer into complete frames. SSE frames are separated by
// a blank line ("\n\n"); the trailing partial chunk (no blank line yet) is kept
// in the buffer for the next read. Returns { frames, rest }.
function splitFrames(buffer) {
  const frames = [];
  let rest = buffer;
  let idx;
  while ((idx = rest.indexOf('\n\n')) !== -1) {
    frames.push(rest.slice(0, idx));
    rest = rest.slice(idx + 2);
  }
  return { frames, rest };
}

// Parse one SSE frame into { event, data }. A frame is a set of lines; we read
// the "event:" name and the "data:" payload (JSON). Tolerates CR and missing
// fields.
function parseFrame(frame) {
  let event = 'message';
  let dataRaw = '';
  for (const lineRaw of frame.split('\n')) {
    const line = lineRaw.replace(/\r$/, '');
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataRaw += line.slice(5).trim();
  }
  let data = {};
  if (dataRaw) {
    try { data = JSON.parse(dataRaw); } catch { data = {}; }
  }
  return { event, data };
}

export default function AvaChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [activity, setActivity] = useState('');
  const [error, setError] = useState('');
  // Single pending image attachment for the next send: { dataUrl, name } | null.
  const [attachment, setAttachment] = useState(null);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const textInputRef = useRef(null);
  // Abort any in-flight stream + guard against post-unmount setState. The Ava
  // tab is forceMount so a tab switch does NOT unmount it, but logging out or
  // leaving /admin does - matching the abortControllerRef idiom in
  // ParsiMapPage.jsx for this same fetch-stream pattern.
  const abortRef = useRef(null);
  const mountedRef = useRef(true);
  // Fire the reload-rehydration effect exactly once, even under StrictMode's
  // deliberate double-invoke of mount effects.
  const hydratedRef = useRef(false);

  useEffect(() => () => {
    mountedRef.current = false;
    abortRef.current?.abort();
  }, []);

  // Rehydrate the transcript after a page reload. The session id survives in
  // localStorage but the message list does not, so a reload used to render
  // blank. On mount, when a stored session exists, pull its history back from
  // the proxy and seed the bubbles in AvaChat's own message shape (timestamps
  // scaled from epoch seconds to ms; assistant text run through extractMedia so
  // MEDIA files render exactly like a live reply).
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    const sessionId = localStorage.getItem(SESSION_KEY);
    if (!sessionId) return;
    (async () => {
      try {
        const token = await getAccessToken();
        if (!token) return;
        const res = await fetch(`/api/ava/sessions/${encodeURIComponent(sessionId)}/messages`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 404) {
          // Session pruned upstream: forget the stale id and stay on the
          // welcome screen, no error UI.
          localStorage.removeItem(SESSION_KEY);
          return;
        }
        if (!res.ok) throw new Error('http-' + res.status);
        const body = await res.json();
        const rows = Array.isArray(body?.messages) ? body.messages : [];
        const seeded = rows.map((r) => {
          const at = r.timestamp ? r.timestamp * 1000 : undefined;
          if (r.role === 'assistant') {
            const { clean, media } = extractMedia(r.content);
            const msg = { role: 'assistant', content: clean, at };
            if (media.length > 0) msg.media = media;
            return msg;
          }
          return { role: 'user', content: typeof r.content === 'string' ? r.content : '', at };
        });
        if (seeded.length === 0) return;
        // Do not race the user: if a turn was sent (or a stream started) while
        // this fetch was in flight, the list is no longer empty - discard the
        // hydration rather than clobber the live conversation.
        setMessages((prev) => (prev.length === 0 ? seeded : prev));
      } catch (e) {
        // Non-fatal: a reload that cannot rehydrate just shows the welcome
        // screen, exactly as before this feature existed.
        console.warn('Ava history hydration failed:', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the transcript pinned to the newest message as it grows.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, activity, streaming]);

  // Auto-grow the composer textarea as the message wraps, up to a max height;
  // it snaps back to a single row once the input is cleared (after send / new
  // chat). Capped so a long paste scrolls inside the box instead of pushing the
  // transcript off-screen.
  useEffect(() => {
    const el = textInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    // scrollHeight is 0 while the tab is display:none (the Ava tab is forceMount
    // but hidden when inactive). Skip then so we never lock the field to 0px and
    // leave it showing a stray scrollbar once the tab is shown again; the CSS
    // min-height keeps it at one row meanwhile. Only enable scrolling once the
    // text actually exceeds the max height.
    const h = el.scrollHeight;
    if (h > 0) {
      el.style.height = Math.min(h, 140) + 'px';
      el.style.overflowY = h > 140 ? 'auto' : 'hidden';
    }
  }, [input]);

  // Ensure a session id exists, creating one lazily on first send. The id is
  // cached in localStorage so the conversation survives reloads and tab
  // switches.
  const ensureSession = useCallback(async (token, signal) => {
    const cached = localStorage.getItem(SESSION_KEY);
    if (cached) return cached;
    const res = await fetch('/api/ava/session', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      signal,
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || !body.session_id) {
      throw new Error(body.error || 'ساخت گفتگو ناموفق بود.');
    }
    localStorage.setItem(SESSION_KEY, body.session_id);
    return body.session_id;
  }, []);

  // Start a fresh conversation: drop the cached session and clear the screen.
  // Open the hidden file picker from the visible attach button.
  const openFilePicker = useCallback(() => {
    if (streaming) return;
    fileInputRef.current?.click();
  }, [streaming]);

  // Drop a starter prompt into the input and focus it so the admin can send or
  // edit it. Ignored mid-stream so we never clobber an in-flight turn.
  const applySuggestion = useCallback((text) => {
    if (streaming) return;
    setInput(text);
    textInputRef.current?.focus();
  }, [streaming]);

  // Handle an image pick: downscale to a JPEG data URL, then stash it as the
  // pending attachment. Reject non-images and over-large encodes with a Persian
  // error. The input is reset so picking the same file twice still fires onChange.
  const onPickImage = useCallback(async (e) => {
    const file = e.target.files && e.target.files[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    if (!file.type || !file.type.startsWith('image/')) {
      setError('فقط می‌توانید تصویر پیوست کنید (png، jpeg، webp، gif).');
      return;
    }
    setError('');
    try {
      const dataUrl = await downscaleImageToDataUrl(file);
      if (dataUrl.length > MAX_DATA_URL_CHARS) {
        setError('این تصویر حتی پس از کوچک‌سازی بسیار بزرگ است. تصویر کوچک‌تری انتخاب کنید.');
        return;
      }
      setAttachment({ dataUrl, name: file.name || 'image' });
    } catch (_) {
      setError('خواندن تصویر ناموفق بود. تصویر دیگری را امتحان کنید.');
    }
  }, []);

  // Drop the pending attachment before sending.
  const removeAttachment = useCallback(() => {
    setAttachment(null);
  }, []);

  const send = useCallback(async () => {
    const text = input.trim();
    const pendingImage = attachment;
    // Allow an image-only turn: send is valid when there is text OR an image.
    if ((!text && !pendingImage) || streaming) return;
    setError('');
    setInput('');
    setAttachment(null);
    setStreaming(true);
    setActivity('');

    // Push the user turn plus an empty assistant turn we will stream into. The
    // image (if any) rides on the user message so the transcript can render it.
    let assistantIndex = -1;
    setMessages((prev) => {
      const now = Date.now();
      const next = [
        ...prev,
        { role: 'user', content: text, image: pendingImage?.dataUrl || null, at: now },
        { role: 'assistant', content: '', at: now },
      ];
      assistantIndex = next.length - 1;
      return next;
    });

    const setAssistant = (updater) => {
      setMessages((prev) => {
        const next = [...prev];
        if (assistantIndex >= 0 && next[assistantIndex]) {
          next[assistantIndex] = { ...next[assistantIndex], content: updater(next[assistantIndex].content) };
        }
        return next;
      });
    };

    // Mirror of the assistant's full reply text, kept alongside the streamed
    // state so that once the turn ends we can pull any MEDIA file tags out of
    // the FINAL text (not a partial mid-stream path).
    let assistantText = '';
    // Whether an SSE error frame arrived. Lets the end-of-stream guard tell an
    // already-reported failure apart from a silent empty completion.
    let sawError = false;

    // Apply one parsed SSE event to component state. Shared by the main read
    // loop and the post-done flush pass so the two stay in sync.
    const handleEvent = (event, data) => {
      if (event === 'assistant.delta') {
        if (data.delta) { assistantText += data.delta; setAssistant((c) => c + data.delta); }
      } else if (event === 'tool.started' || event === 'tool.progress') {
        setActivity('در حال استفاده از ابزار...');
      } else if (event === 'tool.completed' || event === 'tool.failed') {
        setActivity('');
      } else if (event === 'assistant.completed') {
        if (typeof data.content === 'string') { assistantText = data.content; setAssistant(() => data.content); }
        setActivity('');
      } else if (event === 'error') {
        sawError = true;
        setError(data.message || 'خطایی در آوا رخ داد.');
        setActivity('');
      } else if (event === 'done') {
        setActivity('');
      }
    };

    // One AbortController per send: passed into both fetches so unmount (or a
    // future cancel) tears down the upstream hermes run + server SSE pipe
    // instead of leaving them running to completion.
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;

    try {
      const token = await getAccessToken();
      if (!token) throw new Error('برای استفاده از آوا باید وارد شوید.');
      const sessionId = await ensureSession(token, signal);

      const res = await fetch(`/api/ava/session/${encodeURIComponent(sessionId)}/chat/stream`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: buildMessagePayload(text, pendingImage?.dataUrl || null) }),
        signal,
      });
      if (!res.ok || !res.body) {
        throw new Error('ارتباط با آوا برقرار نشد.');
      }

      const reader = res.body.getReader();
      const decoder = typeof TextDecoder !== 'undefined' ? new TextDecoder() : null;
      let buffer = '';

      // Pump the stream. Each read may carry several SSE frames or a partial
      // one; we buffer and split on the blank-line frame boundary.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        // Bail if we unmounted mid-stream so we never setState on a gone
        // component (the cleanup effect has already aborted the fetch).
        if (!mountedRef.current) break;
        const { value, done } = await reader.read();
        if (done) {
          // Final no-arg flush in case a multi-byte UTF-8 sequence straddled
          // the last chunk boundary; then drain any complete trailing frame.
          if (decoder) buffer += decoder.decode();
          const tail = splitFrames(buffer);
          buffer = tail.rest;
          for (const raw of tail.frames) {
            if (!raw.trim()) continue;
            const { event, data } = parseFrame(raw);
            handleEvent(event, data);
          }
          break;
        }
        // jsdom test envs may hand back already-decoded strings; only decode
        // real byte chunks.
        const chunk = typeof value === 'string'
          ? value
          : (decoder ? decoder.decode(value, { stream: true }) : String(value || ''));
        buffer += chunk;

        const { frames, rest } = splitFrames(buffer);
        buffer = rest;
        for (const raw of frames) {
          if (!raw.trim()) continue;
          const { event, data } = parseFrame(raw);
          handleEvent(event, data);
        }
      }

      // Turn finished: lift any MEDIA file tags out of the final reply, replace
      // the displayed text with the tag-free version, and hand the file list to
      // the message so <AvaMedia> can fetch + render each one.
      if (mountedRef.current) {
        const { clean, media } = extractMedia(assistantText);
        if (media.length > 0) {
          setMessages((prev) => {
            const next = [...prev];
            if (assistantIndex >= 0 && next[assistantIndex]) {
              next[assistantIndex] = { ...next[assistantIndex], content: clean, media };
            }
            return next;
          });
        } else if (!clean && !sawError) {
          // The stream ended with no text, no file and no error frame. This is
          // what a backend auth/connection failure looks like from the browser
          // (the upstream aborts and pipes an empty body). Drop the blank
          // assistant bubble and show a clear Persian notice so the chat never
          // looks frozen with no explanation.
          setMessages((prev) => prev.filter((_, idx) => idx !== assistantIndex));
          setError('آوا پاسخی برنگرداند. ممکن است ارتباط یا احراز هویت دستیار موقتا برقرار نباشد. لطفا کمی بعد دوباره تلاش کنید.');
        }
      }
    } catch (e) {
      // A deliberate abort (unmount / leaving /admin) is not a user-facing
      // error - swallow it and let the cleanup effect handle teardown.
      if (e.name === 'AbortError') return;
      if (mountedRef.current) setError(e.message || 'خطایی رخ داد.');
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      if (mountedRef.current) {
        setStreaming(false);
        setActivity('');
      }
    }
  }, [input, attachment, streaming, ensureSession]);

  // Enter sends; Shift+Enter inserts a newline. Ignore Enter while an IME
  // composition is in progress so composing on a soft keyboard is not cut off.
  const onInputKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent?.isComposing) {
      e.preventDefault();
      send();
    }
  }, [send]);

  const onSubmit = (e) => {
    e.preventDefault();
    send();
  };

  const lastIndex = messages.length - 1;

  return (
    <div className="ava-chat" dir="rtl">
      <header className="ava-header">
        <div className="ava-identity">
          <img className="ava-avatar" src={avaAvatar} alt="آوا" />
          <span className="ava-identity-text">
            <span className="ava-identity-name">آوا</span>
            <span className="ava-identity-sub">
              <span className="ava-status-dot" aria-hidden="true" />
              دستیار هوشمند پنل مدیریت
            </span>
          </span>
        </div>
        {/* New-chat moved to the AvaTab subtab strip (ava-newchat) - one control,
            always labeled, visible from both the chat and history tabs. */}
      </header>

      {/* No aria-live on the transcript itself: assistant text grows token by
          token, so announcing the whole transcript would spam the screen
          reader on every delta. Only the short status line below is a live
          region. */}
      <div className="ava-transcript" ref={scrollRef}>
        {messages.length === 0 ? (
          <div className="ava-empty">
            <img className="ava-empty-avatar" src={avaAvatar} alt="آوا" />
            <h3 className="ava-empty-title">با آوا گفتگو کنید</h3>
            <p className="ava-empty-sub">
              دستیار هوشمند سامانهٔ FTTX. سوال بپرسید، تصویر بفرستید یا گزارش بخواهید.
            </p>
            <div className="ava-suggestions">
              {SUGGESTIONS.map((s) => (
                <button key={s} type="button" className="ava-suggestion" onClick={() => applySuggestion(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m, i) => {
            // Brief in-bubble typing dots for the assistant turn we are about to
            // stream into, before the first token lands (and only when no
            // separate tool-use status is already showing).
            const showTyping =
              streaming && i === lastIndex && m.role === 'assistant' && !m.content && !activity
              && !(Array.isArray(m.media) && m.media.length > 0);
            return (
              <div key={i} className={`ava-msg ava-msg-${m.role}`}>
                <div className="ava-msg-head">
                  <span className="ava-msg-role">{m.role === 'user' ? 'شما' : 'آوا'}</span>
                  {m.at && <span className="ava-msg-time">{formatTime(m.at)}</span>}
                </div>
                {m.image && (
                  <img className="ava-msg-image" src={m.image} alt="تصویر پیوست‌شده" />
                )}
                {m.content && <div className="ava-msg-content">{m.content}</div>}
                {showTyping && (
                  <span className="ava-typing" aria-hidden="true"><i /><i /><i /></span>
                )}
                {Array.isArray(m.media) && m.media.length > 0 && (
                  <div className="ava-msg-media">
                    {m.media.map((md, j) => <AvaMedia key={md.path || j} item={md} />)}
                  </div>
                )}
              </div>
            );
          })
        )}
        {activity && (
          <div className="ava-activity">
            <span className="ava-typing" aria-hidden="true"><i /><i /><i /></span>
            <span>{activity}</span>
          </div>
        )}
      </div>

      {/* Scoped polite live region for the tool-use status only - kept out of
          the transcript so streamed deltas are not re-announced per token. */}
      <div className="ava-visually-hidden" aria-live="polite">{activity}</div>

      <div className="ava-composer-wrap">
        {error && <div className="ava-error" role="alert">{error}</div>}

        {attachment && (
          <div className="ava-attach-strip">
            <div className="ava-attach-thumb">
              <img className="ava-attach-img" src={attachment.dataUrl} alt="پیش‌نمایش تصویر پیوست" />
              <button
                type="button"
                className="ava-attach-remove"
                onClick={removeAttachment}
                aria-label="حذف تصویر پیوست"
                disabled={streaming}
              >
                ×
              </button>
            </div>
            <span className="ava-attach-name" title={attachment.name}>{attachment.name}</span>
          </div>
        )}

        <form className="ava-composer" onSubmit={onSubmit}>
          <label htmlFor="ava-input" className="ava-visually-hidden">پیام برای آوا</label>
          <input
            ref={fileInputRef}
            className="ava-visually-hidden"
            type="file"
            accept="image/*"
            onChange={onPickImage}
            tabIndex={-1}
            aria-hidden="true"
          />
          <button
            type="button"
            className="ava-attach-btn"
            onClick={openFilePicker}
            disabled={streaming}
            aria-label="پیوست تصویر"
            title="پیوست تصویر"
          >
            <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
              <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
          </button>
          <textarea
            id="ava-input"
            ref={textInputRef}
            className="ava-input"
            dir="rtl"
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder="پیامی برای آوا بنویسید..."
            disabled={streaming}
            autoComplete="off"
          />
          <button type="submit" className="ava-send" disabled={streaming || (!input.trim() && !attachment)}>
            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
              <path d="M22 2 11 13" />
              <path d="M22 2 15 22l-4-9-9-4 20-7z" />
            </svg>
            <span className="ava-send-label">{streaming ? 'در حال ارسال...' : 'ارسال'}</span>
          </button>
        </form>
      </div>
    </div>
  );
}
